import {
  AuthError,
  authorizationHeader,
  type AuthClient,
  type AuthErrorKind,
  type Credentials,
  type Registration,
  type Session,
} from './auth-client';
import { readProblem } from '../net/problem-json';

export class HttpAuthClient implements AuthClient {
  constructor(private readonly baseUrl: string) {}

  async login(credentials: Credentials): Promise<Session> {
    return this.postForSession('/api/auth/login', {
      username: credentials.username,
      password: credentials.password,
    });
  }

  async register(registration: Registration): Promise<Session> {
    return this.postForSession('/api/auth/register', {
      username: registration.username,
      email: registration.email,
      password: registration.password,
      referrer: registration.referrer ?? null,
    });
  }

  async logout(session: Session): Promise<void> {
    try {
      await fetch(this.url('/api/auth/logout'), {
        method: 'POST',
        credentials: 'omit',
        headers: { authorization: authorizationHeader(session) },
      });
    } catch {
      // deliberately swallowed
    }
  }

  async currentSession(session: Session): Promise<Session> {
    const response = await this.send(this.url('/api/auth/session'), {
      method: 'GET',
      credentials: 'omit',
      headers: { authorization: authorizationHeader(session) },
    });

    if (!response.ok) throw await problemToError(response);

    const body = (await response.json()) as {
      accountId?: number;
      username?: string | null;
      expiresAt?: string;
    };

    return {
      ...session,
      accountId: body.accountId ?? session.accountId,
      username: body.username ?? session.username,
      expiresAt: body.expiresAt !== undefined ? new Date(body.expiresAt) : session.expiresAt,
    };
  }

  private async postForSession(path: string, body: unknown): Promise<Session> {
    const response = await this.send(this.url(path), {
      method: 'POST',
      credentials: 'omit',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw await problemToError(response);

    const parsed = (await response.json()) as Partial<{
      token: string;
      tokenType: string;
      expiresAt: string;
      accountId: number;
      legacyId: number | null;
      username: string;
    }>;

    if (
      typeof parsed.token !== 'string' ||
      typeof parsed.expiresAt !== 'string' ||
      typeof parsed.accountId !== 'number' ||
      typeof parsed.username !== 'string'
    ) {
      throw new AuthError(
        'The server returned a session without the fields a session needs.',
        'server-error',
        response.status,
      );
    }

    return {
      token: parsed.token,
      tokenType: parsed.tokenType ?? 'Bearer',
      expiresAt: new Date(parsed.expiresAt),
      accountId: parsed.accountId,
      legacyId: parsed.legacyId ?? null,
      username: parsed.username,
    };
  }

  private url(path: string): string {
    return `${this.baseUrl.replace(/\/+$/, '')}${path}`;
  }

  private async send(url: string, init: RequestInit): Promise<Response> {
    try {
      return await fetch(url, init);
    } catch (cause) {
      const error = new AuthError(
        'Could not reach the server. Check your connection and try again.',
        'unreachable',
      );
      error.cause = cause;
      throw error;
    }
  }
}

async function problemToError(response: Response): Promise<AuthError> {
  const kind = kindForStatus(response.status);
  const problem = await readProblem(response);
  return new AuthError(
    problem.detail ?? defaultMessage(kind),
    kind,
    response.status,
    problem.code,
    problem.fieldErrors,
  );
}

function kindForStatus(status: number): AuthErrorKind {
  if (status === 401) return 'invalid-credentials';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate-limited';
  if (status === 400 || status === 422) return 'invalid-request';
  if (status === 403) return 'session-invalid';
  return 'server-error';
}

function defaultMessage(kind: AuthErrorKind): string {
  switch (kind) {
    case 'invalid-credentials':
      /** One message for both cases: naming which half was wrong tells an attacker which usernames exist. */
      return 'Username or password incorrect.';
    case 'conflict':
      return 'That username or e-mail is already taken.';
    case 'rate-limited':
      return 'Too many attempts. Wait a moment and try again.';
    case 'invalid-request':
      return 'Some of those details were not accepted.';
    case 'session-invalid':
      return 'Your session has expired. Log in again.';
    case 'unreachable':
      return 'Could not reach the server. Check your connection and try again.';
    case 'server-error':
      return 'Something went wrong on our side. Try again shortly.';
  }
}
